import React, { useCallback, useMemo, useState } from 'react';
import { DataGrid as BaseDataGrid } from '@/components/DataGrid/DataGrid';
import type { ColumnConfig, DataGridProps, GridConfig } from '@/components/DataGrid/types';
import '@/modules/utility/ds-wms/styles/wmsTable.css';

/**
 * Every WMS grid renders through this wrapper so the module's tables share one look —
 * the register look of Asset Management and the Finance inbox: soft indigo header at
 * compact height with ruled cells, a leading S.No column, right-aligned figures, and
 * (for the full variant) the search box, column chooser, per-column filter row,
 * drag-to-group panel, Excel/PDF/CSV export, and a closing "Total · N rows" row that
 * sums the money / quantity columns.
 *
 * It stays on the global `@/components/DataGrid` rather than the SDK copy the asset
 * module uses: this copy formats currency as INR by default, the SDK copy as USD, and
 * 14 WMS screens rely on the INR default.
 *
 * `variant="embedded"` is for grids that sit inside a form or picker (SOR / rate-contract
 * selectors, tender steps): same header and columns, but the grid's own toolbar
 * settings are kept and no total row is added, since that chrome over a pick-list is clutter.
 */
export type WmsDataGridProps = DataGridProps & { variant?: 'full' | 'embedded' };

/**
 * Header shape only. Colours and rules (indigo header, dark 2px top/bottom line,
 * ruled cells, indigo total row) come from styles/wmsTable.css via the `.wms-grid`
 * wrapper, so they follow dark mode — a pinned `headerBackgroundColor` would not.
 */
export const WMS_GRID_HEADER = {
  style: 'modern' as const,
  showBorders: true,
  headerHeight: 'compact' as const,
};

const SNO_FIELD = '__wms_sno';

const SNO_NAME = /^(s\.?\s*no\.?|sl\.?\s*no\.?|sr\.?\s*no\.?|#|serial(\s*no\.?|\s*number)?)$/i;

const isSerial = (c: ColumnConfig) =>
  c.field === 'sno' || c.field === SNO_FIELD || SNO_NAME.test((c.columnName || '').trim());

const SNO_COLUMN: ColumnConfig = {
  field: SNO_FIELD,
  columnName: 'S.No',
  columnType: 'number',
  width: '60px',
  alignment: 'center',
  visible: true,
  filter: false,
  search: false,
  allowGrouping: false,
  exportable: true,
};

/*
 * Numeric columns whose sum means nothing — a total of depths, minimums, sequence
 * numbers or percentages reads as data and is not. Everything else numeric (amounts,
 * areas, lengths, counts) is totalled.
 */
const NO_SUM_NAME = /(%|percent|\bpct\b|\brate\b|ratio|year|\bdays?\b|month|validity|depth|width|\bmin\b|\bmax\b|\bseq|sequence|\bqty\b|quantity|\bavg\b|average|progress|\blat|\blong|\blng\b|\bid\b|code|rank|score)/i;
const NO_SUM_FIELD = /(^id$|_id$|_cd$|sqnce|pct|prcnt|prgrs|progress|_dy$|days|mnths|depth|width|^min_|^max_|qty|_rt$|rate|year|_yr$)/i;

const isFigure = (c: { columnType?: string }) => c.columnType === 'number' || c.columnType === 'currency';

/** Figures read right-aligned, as in a printed register — unless the grid chose otherwise. */
const alignFigures = (columns: ColumnConfig[]): ColumnConfig[] =>
  columns.map(c => {
    const next = c.subColumns ? { ...c, subColumns: c.subColumns.map(s =>
      s.alignment || !isFigure(s) ? s : { ...s, alignment: 'right' as const }) } : c;
    if (next.alignment || !isFigure(next)) return next;
    return { ...next, alignment: 'right' as const };
  });

export const WmsDataGrid: React.FC<WmsDataGridProps> = ({ variant = 'full', ...props }) => {
  const { dataSource, columns, gridConfig, manualPagination, pageIndex, onPaginationChange } = props;
  const full = variant === 'full';

  const addSerial = !(columns || []).some(isSerial);

  // Server-paged grids hold one page, so the serial number has to start at that page's offset.
  const [pageSize, setPageSize] = useState<number>(gridConfig?.defaultPageSize || gridConfig?.minPageSizes?.[0] || 10);
  const handlePaginationChange = useCallback((p: { pageIndex: number; pageSize: number }) => {
    setPageSize(p.pageSize);
    onPaginationChange?.(p);
  }, [onPaginationChange]);

  const rows = useMemo(() => {
    if (!addSerial || !Array.isArray(dataSource)) return dataSource;
    const offset = manualPagination ? (pageIndex || 0) * pageSize : 0;
    return dataSource.map((row, i) => ({ ...row, [SNO_FIELD]: offset + i + 1 }));
  }, [addSerial, dataSource, manualPagination, pageIndex, pageSize]);

  const rowCount = Array.isArray(dataSource) ? dataSource.length : 0;

  /*
   * The closing total row. A server-paged grid only holds the current page, so its
   * sums are a page total and say so; a client-side grid holds every row.
   */
  const totalLabel = useMemo(() => {
    const noun = (n: number) => (n === 1 ? 'row' : 'rows');
    const all = gridConfig?.totalRowCount;
    if (manualPagination && all != null && all > rowCount) {
      return `Page total · ${rowCount.toLocaleString('en-IN')} of ${all.toLocaleString('en-IN')} ${noun(all)}`;
    }
    return `Total · ${rowCount.toLocaleString('en-IN')} ${noun(rowCount)}`;
  }, [gridConfig?.totalRowCount, manualPagination, rowCount]);

  const cols = useMemo(() => {
    let out = alignFigures(columns || []);
    if (full) {
      const sample = Array.isArray(dataSource) ? dataSource[0] : undefined;
      // Filter / sum only a column that names a real field on the row — a computed
      // column's dropdown would offer one empty option and filter everything away.
      const isField = (f?: string) => !!f && !!sample && Object.prototype.hasOwnProperty.call(sample, f);
      out = out.map(c => {
        const next = { ...c };
        if (next.filter === undefined) next.filter = isField(next.field);
        if (!next.summaryType && !isSerial(next) && isFigure(next) && isField(next.field)
          && !NO_SUM_NAME.test(next.columnName || '') && !NO_SUM_FIELD.test(next.field || '')) {
          next.summaryType = 'sum';
        }
        return next;
      });
      // "Total · N rows" sits under the first data column, as long as that column is
      // free to carry it (not a figure that is itself being totalled).
      if (rowCount > 0) {
        const at = out.findIndex(c => !isSerial(c) && c.visible !== false && !c.subColumns);
        if (at >= 0 && !out[at].summaryType) {
          out[at] = { ...out[at], summaryType: 'count', summaryRenderer: () => totalLabel };
        }
      }
    }
    return addSerial ? [SNO_COLUMN, ...out] : out;
  }, [addSerial, columns, dataSource, full, rowCount, totalLabel]);

  const config = useMemo<GridConfig>(() => {
    const base: GridConfig = { ...gridConfig, headerCustomization: { ...WMS_GRID_HEADER } };
    if (!full) return base;
    // The serial cell of the total row stays blank; the label is under the first data column.
    const labelled = cols.some(c => c.summaryType === 'count' && c.summaryRenderer);
    return {
      pagination: true,
      defaultPageSize: 10,
      minPageSizes: [10, 25, 50, 100],
      enableColumnResizing: true,
      ...base,
      enableSearchPanel: true,
      enableColumnChooser: true,
      enableColumnFilters: true,
      enableGroupingPanel: true,
      summaryRowLabel: labelled ? '' : 'Total',
      export: { formats: ['excel', 'pdf', 'csv'], ...gridConfig?.export, enabled: true },
    };
  }, [gridConfig, full, cols]);

  // `.wms-grid` is display: contents — it scopes the stylesheet without adding a box,
  // so grids sized by a flex / fixed-height parent lay out exactly as before.
  return (
    <div className="wms-grid">
      <BaseDataGrid
        {...props}
        dataSource={rows}
        columns={cols}
        gridConfig={config}
        onPaginationChange={(onPaginationChange || manualPagination) ? handlePaginationChange : undefined}
      />
    </div>
  );
};

export { WmsDataGrid as DataGrid };
export default WmsDataGrid;
